import axios from 'axios';

const API = axios.create({
  baseURL: '/api',
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
});

// Слушатели «сессия закончилась»: App показывает форму входа, не теряя страницу.
const unauthorizedHandlers = new Set();
export const onUnauthorized = (fn) => {
  unauthorizedHandlers.add(fn);
  return () => unauthorizedHandlers.delete(fn);
};

// Response interceptor: log errors, retry once on 5xx
API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    if (error.response && error.response.status >= 500 && !config._retried) {
      config._retried = true;
      console.warn(`[API] 5xx error on ${config.url}, retrying...`);
      return API(config);
    }
    if (error.response) {
      // Сессия закончилась, только если DRF ответил своим `detail` (нет учётных данных).
      // Наши прикладные 403 (закрытая по расписанию тема, чужой объект) несут `error` — их не трогаем.
      const body = error.response.data || {};
      if (error.response.status === 403 && body.detail && !body.error
          && !String(config.url || '').includes('/auth/')) {
        unauthorizedHandlers.forEach((fn) => fn());
      }
      // 403 на /auth/me/ у неавторизованного гостя — норма, а не ошибка: не засоряем консоль
      const expected = error.response.status === 403 && String(config.url || '').includes('/auth/me');
      if (!expected) {
        console.error(`[API] ${error.response.status} ${config.method?.toUpperCase()} ${config.url}`);
      }
    } else {
      console.error(`[API] Network error: ${config.url}`);
    }
    return Promise.reject(error);
  }
);

// --- Auth ---
export const login = (username, password) => API.post('/auth/login/', { username, password });
export const logout = () => API.post('/auth/logout/');
export const fetchMe = () => API.get('/auth/me/');
export const changePassword = (newPassword1, newPassword2) =>
  API.post('/auth/password/', { new_password1: newPassword1, new_password2: newPassword2 });

// --- Schedule, course, homework ---
export const fetchLectures = () => API.get('/course/lectures/');
export const fetchLecture = (n) => API.get(`/course/lectures/${n}/`);
// Скачивание идёт обычной ссылкой (кука сессии уходит сама), поэтому нужен только адрес
export const lectureMaterialUrl = (n, name) =>
  `/api/course/lectures/${n}/materials/${name.split('/').map(encodeURIComponent).join('/')}`;
export const fetchHomeworks = () => API.get('/homework/');
export const fetchHomework = (n) => API.get(`/homework/${n}/`);
export const saveHomeworkDraft = (n, solution, notebook) =>
  API.put(`/homework/${n}/submission/`, { solution, notebook });
export const submitHomework = (n, solution, notebook) =>
  API.post(`/homework/${n}/submission/`, { solution, notebook });

// --- Personal space ---
export const fetchMySpace = () => API.get('/me/space/');
export const refreshMySpace = () => API.post('/me/space/refresh/');
export const fetchLeaderboard = () => API.get('/leaderboard/');
export const generateMeme = () => API.post('/standup/meme/');

// --- Client events (метрики поведения студента) ---
export const logEvent = (type, payload = {}, sessionId = null) =>
  API.post('/events/', { type, payload, session_id: sessionId }).catch(() => {});

// --- Tasks & Topics ---
export const fetchTopics = () => API.get('/topics/');
export const fetchTopic = (id) => API.get(`/topics/${id}/`);
export const fetchTask = (id) => API.get(`/tasks/${id}/`);
export const fetchRelatedLemmas = (taskId) => API.get(`/tasks/${taskId}/related/`);
export const fetchRandomTasks = (count = 5) => API.get('/tasks/random/', { params: { count } });

// --- Lean ---
export const warmLean = () => API.post('/lean/warm/');

export const verifyLean = (leanCode, sessionId) =>
  API.post('/lean/verify/', { lean_code: leanCode, session_id: sessionId });

// --- Chat ---
export const sendChatMessage = (message, taskId, sessionId) =>
  API.post('/chat/', { message, task_id: taskId, session_id: sessionId });

/** CSRF-токен из cookie: axios подставляет его сам, а ручному fetch нужно передать явно. */
export function csrfToken() {
  const match = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : '';
}

/**
 * Стрим ответа ассистента (SSE поверх POST). onDelta(text) вызывается на каждый фрагмент.
 * Возвращает финальное событие {done, mcp_context, tokens}; бросает Error с текстом от сервера.
 */
export async function streamChatMessage(message, taskId, sessionId, onDelta) {
  const res = await fetch('/api/chat/', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken() },
    body: JSON.stringify({ message, task_id: taskId, session_id: sessionId, stream: true }),
  });
  if (!res.ok) {
    // Сервер объясняет причину в теле: исчерпанная квота, закрытая задача, конец сессии
    let reason = '';
    try {
      const body = await res.json();
      reason = body.error || body.response || body.detail || '';
    } catch { /* тело не JSON */ }
    if (res.status === 403 && !reason) reason = 'Сессия закончилась — войдите заново.';
    throw new Error(reason || `Ассистент не ответил (${res.status}).`);
  }
  if (!res.body) throw new Error('Пустой ответ ассистента.');
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let final = null;
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buffer.indexOf('\n\n')) >= 0) {
      const frame = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const line = frame.split('\n').find((l) => l.startsWith('data: '));
      if (!line) continue;
      const evt = JSON.parse(line.slice(6));
      if (evt.delta) onDelta(evt.delta);
      else if (evt.error) throw new Error(evt.error);
      else if (evt.done) final = evt;
    }
  }
  return final;
}

// --- Sessions ---
export const createSession = (taskId) => API.post('/sessions/', { task: taskId });

// --- Task progress ---
export const getTaskProgress = (taskId) => API.get(`/tasks/${taskId}/progress/`);

export const updateTaskProgress = (taskId, data) =>
  API.put(`/tasks/${taskId}/progress/`, data);

// --- MathLib Explorer ---
export const searchMathlib = (query, limit = 20, mode = 'semantic', modality = null) =>
  API.post('/mathlib/search/', { query, limit, mode, modality });

export const getMathLibStatement = (statementId) =>
  API.get(`/mathlib/statement/${statementId}/`);

export const getMathLibGraph = (uri) =>
  API.post('/mathlib/graph/', { uri });

export const getMathLibSimilar = (uri, limit = 5, statementId = null) =>
  API.post('/mathlib/similar/', { uri, limit, statement_id: statementId });

// --- Error detection ---
export const generateErrorSolution = (taskId, difficulty = 1) =>
  API.post('/chat/generate-errors/', { task_id: taskId, difficulty });

export const evaluateErrors = (answerKey, studentAnalysis) =>
  API.post('/chat/evaluate-errors/', { answer_key: answerKey, student_analysis: studentAnalysis });

// --- Lab (task generation) ---
export const labGenerate = (topic, difficulty = 2) =>
  API.post('/lab/generate/', { topic, difficulty });

export const labCheck = (task, correctAnswer, studentAnswer) =>
  API.post('/lab/check/', { task, correct_answer: correctAnswer, student_answer: studentAnswer });

export const fetchArchive = (kind = null, page = 1, pageSize = 20) =>
  API.get('/archive/', { params: { ...(kind ? { kind } : {}), page, page_size: pageSize } });

// --- Client errors (ErrorBoundary, window.onerror) ---
export const reportClientError = (payload) => API.post('/client-errors/', payload);

export default API;
